import { FieldTimeOutlined, WarningFilled } from '@ant-design/icons'
import { DisableUntilToggle } from './DisableUntilToggle'
import { BBDTag, DeltaTag, GraviButton, Texto } from '@gravitate-js/excalibrr'
import { DateTime } from '@utils/cell_formatter'
import { Badge, Progress, Tag, Tooltip } from 'antd'
import React from 'react'
import NumberFormat from 'react-number-format'

import { antDColumns } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'

export const hiddenColumns = [
  'children',
  'key',
  'id',
  'timezone',
  'daily_pace',
  'weekly_pace',
  'delta_to_contract',
  'delta_to_inventory',
  'index',
  'children_count',
  'disabled_until',
  'product_group',
  'curve_id',
  'group_id',
  'group_name',
  'created_date',
  'reliability',
  'updated_by',
  'updated_on',
  'disabled_reason',
  'disabled_by',
  'supply_zone',
  'city',
  'effective_from_local',
  'effective_to_local',
  'expire_local',
  'timezone_data',
  'product_id',
  'terminal_id',
  'supplier_id',
  'counterparty_id',
]
export const columnOrder = [
  'enabled',
  'product',
  'terminal',
  'supply_zone',
  'city',
  'supplier',
  'price_type',
  'price',
  'rank',
  'delta_to_low',
  'effective_from',
  'effective_to',
  'contract',
  'min_quantity',
  'max_quantity',
]

export const columnConfig = (toggleDrawer, showCarrierView, formattedCurrency) => {
  if (showCarrierView) {
    hiddenColumns.push('price', 'min_quantity', 'max_quantity')
  }
  return {
    enabled: {
      render: (checked, record) => (
        <>
          {showCarrierView ? (
            <div style={{ float: 'right', marginRight: '3em' }}>
              {record.enabled ? <BBDTag success>Enabled</BBDTag> : <BBDTag error>Disabled</BBDTag>}
            </div>
          ) : (
            <>
              <DisableUntilToggle checked={checked} record={record} />
              <Tooltip title='View Price History'>
                <GraviButton
                  icon={<FieldTimeOutlined />}
                  style={{ float: 'right', marginRight: '1em' }}
                  theme2
                  appearance='outline'
                  onClick={() => toggleDrawer(record)}
                />
              </Tooltip>
            </>
          )}
        </>
      ),
      width: 200,
      title: '',
    },
    rank: {
      render: (data) => <Badge count={data} className='grid-circle-number' />,
    },
    price_type: {
      render: (data, row) => (
        <Texto weight={row.enabled ? 'bold' : ''} className={row.enabled ? '' : 'font-italic'}>
          {data.toUpperCase()}
        </Texto>
      ),
    },

    product: {
      render: (value, row) => (
        <Texto weight={row.enabled ? 'bold' : ''} className={row.enabled ? '' : 'font-italic'}>
          {value}
        </Texto>
      ),
    },

    terminal: {
      render: (value, row) => <Texto className={row.enabled ? '' : 'font-italic'}>{value}</Texto>,
    },
    supplier: {
      ellipsis: true,
      render: (value, row) => <Texto className={row.enabled ? '' : 'font-italic'}>{value}</Texto>,
    },
    price: {
      render: (data, row) => (
        <Texto weight={row.enabled ? 'bold' : ''} className={row.enabled ? '' : 'font-italic'}>
          <NumberFormat
            value={data}
            thousandSeparator
            prefix={formattedCurrency}
            displayType='text'
            decimalScale={4}
            fixedDecimalScale
          />
        </Texto>
      ),
    },
    delta_to_low: {
      render: (data) => (
        <DeltaTag value={data} threshold={0} textIfEqual='Low Price' prefix={formattedCurrency} precision={4} />
      ),
    },
    effective_from: {
      render: (data, row) => (data ? formatters.DATE_TIME(data, row.effective_from_local) : ''),
      width: 200,
    },
    created_date: {
      render: (data) => <DateTime data={data} />,
    },
    expire: {
      render: (data, row) => {
        return data ? formatters.DATE_TIME(data, row.expire_local) : ''
      },
    },
    expired_warning: {
      render: (data) => {
        if (data)
          return (
            <Texto appearance='warning' category='h6'>
              <Tooltip title='Expired Price'>
                <WarningFilled />
              </Tooltip>
            </Texto>
          )
        return <></>
      },
    },
    effective_to: {
      render: (data, row) => (data ? formatters.DATE_TIME(data, row.effective_to_local) : ''),
      width: 200,
    },
    contract: {
      render: (data) => (data && data !== '' ? <Tag className='tag-link'>{data}</Tag> : ''),
    },
    monthly_pace: {
      render: (data) => <Progress percent={data * 100} size='small' />,
    },
    min_quantity: {
      editable: true,
    },
    max_quantity: {
      editable: true,
    },
  }
}
export const getHistoryColumns = (firstRow, ignoredColumns) => {
  const columnOrder = [
    'effective_from',
    'effective_to',
    'price',
    'rank',
    'delta_to_low',
    'reliability',
    'contract',
    'min_quantity',
    'max_quantity',
    'created_date',
  ]
  let ignoreOverride = [
    ...ignoredColumns,
    'enabled',
    'children',
    'product',
    'terminal',
    'supplier',
    'price_type',
    'delta_to_low',
    'rank',
    'contract',
    'reliability',
    'loaded',
    'scheduled',
    'planned',
    'carrier',
    'total_with_planned',
    'total_no_planned',
    'freight',
    'total_price',
    'loaded_miles',
    'loaded_minutes',
    'directive_min',
    'directive_max',
    'multiple_prices',
    'remaining',
    'remaining_with_planned',
    'directive',
    'outages',
    'status',
  ]
  ignoreOverride = ignoreOverride.filter((i) => i !== 'created_date')
  const rowTemplate = { ...firstRow }
  delete rowTemplate.children
  return antDColumns([firstRow], columnConfig(), ignoreOverride, [], columnOrder)
}
